/**
 * FERP Portal - Annotation Configuration (DRR-062)
 *
 * Data-driven UI configuration for the Contextual Annotation System.
 * Frontend is DUMB — this only provides display properties for backend-defined enums.
 *
 * Source of truth: graphql_gateway/src/annotation_api/annotation_model.rs
 *
 * DO NOT add new annotation types or visibility levels here without backend changes first.
 */

import {
  IconMessage,
  IconFileText,
  IconAlertTriangle,
  IconCircleCheck,
  IconEye,
  IconEyeOff,
  IconLock,
  type Icon,
} from '@tabler/icons-react';

// ============================================================================
// ANNOTATION TYPE CONFIG
// ============================================================================

/**
 * Annotation types as defined by backend GqlAnnotationType enum.
 * String values match backend exactly — do NOT use imported enum until codegen runs.
 */
export const ANNOTATION_TYPE = {
  COMMENT: 'COMMENT',
  NOTE: 'NOTE',
  ESCALATION: 'ESCALATION',
  DECISION: 'DECISION',
} as const;

export type AnnotationTypeKey = keyof typeof ANNOTATION_TYPE;

export interface AnnotationTypeConfig {
  label: string;
  color: string;
  icon: Icon;
  description: string;
  /** Whether creating this type shows a confirmation dialog */
  requiresConfirmation: boolean;
  /** Sort priority (higher = more important, shown first) */
  priority: number;
  /** Required scope to create this type. null = any user with annotation:write */
  requiredScope: string | null;
}

/**
 * UI configuration for each AnnotationType.
 *
 * Colors follow severity hierarchy consistent with SAFETY_LEVEL_UI_CONFIG:
 * - COMMENT: blue (standard conversation)
 * - NOTE: cyan (informational record)
 * - ESCALATION: red (requires immediate attention, life-safety relevant)
 * - DECISION: green (formal decision record for audit trail)
 */
export const ANNOTATION_TYPE_UI_CONFIG: Record<
  AnnotationTypeKey,
  AnnotationTypeConfig
> = {
  COMMENT: {
    label: 'Comment',
    color: 'blue',
    icon: IconMessage,
    description: 'General discussion or feedback',
    requiresConfirmation: false,
    priority: 1,
    requiredScope: null,
  },
  NOTE: {
    label: 'Note',
    color: 'cyan',
    icon: IconFileText,
    description: 'Informational record for context',
    requiresConfirmation: false,
    priority: 2,
    requiredScope: null,
  },
  ESCALATION: {
    label: 'Escalation',
    color: 'red',
    icon: IconAlertTriangle,
    description: 'Requires immediate attention — triggers notification',
    requiresConfirmation: true,
    priority: 4,
    requiredScope: 'annotation:escalate',
  },
  DECISION: {
    label: 'Decision',
    color: 'green',
    icon: IconCircleCheck,
    description: 'Formal decision for audit trail',
    requiresConfirmation: true,
    priority: 3,
    requiredScope: 'annotation:admin',
  },
};

// ============================================================================
// ANNOTATION VISIBILITY CONFIG
// ============================================================================

export const ANNOTATION_VISIBILITY = {
  INTERNAL: 'INTERNAL',
  EXTERNAL: 'EXTERNAL',
  RESTRICTED: 'RESTRICTED',
} as const;

export type AnnotationVisibilityKey = keyof typeof ANNOTATION_VISIBILITY;

export interface AnnotationVisibilityConfig {
  label: string;
  color: string;
  icon: Icon;
  description: string;
}

/**
 * UI configuration for each AnnotationVisibility level.
 */
export const ANNOTATION_VISIBILITY_UI_CONFIG: Record<
  AnnotationVisibilityKey,
  AnnotationVisibilityConfig
> = {
  INTERNAL: {
    label: 'Internal',
    color: 'gray',
    icon: IconEye,
    description: 'Visible to all internal team members',
  },
  EXTERNAL: {
    label: 'External',
    color: 'blue',
    icon: IconEyeOff,
    description: 'Visible to external parties (patients, clients)',
  },
  RESTRICTED: {
    label: 'Restricted',
    color: 'orange',
    icon: IconLock,
    description: 'Visible only to specified roles',
  },
};

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Get annotation type config with safe fallback.
 * Returns COMMENT config for unknown types.
 */
export function getAnnotationTypeConfig(
  annotationType: string,
): AnnotationTypeConfig {
  return (
    ANNOTATION_TYPE_UI_CONFIG[annotationType as AnnotationTypeKey] ??
    ANNOTATION_TYPE_UI_CONFIG.COMMENT
  );
}

/**
 * Get annotation visibility config with safe fallback.
 * Returns INTERNAL config for unknown visibility levels.
 */
export function getAnnotationVisibilityConfig(
  visibility: string,
): AnnotationVisibilityConfig {
  return (
    ANNOTATION_VISIBILITY_UI_CONFIG[visibility as AnnotationVisibilityKey] ??
    ANNOTATION_VISIBILITY_UI_CONFIG.INTERNAL
  );
}

// ============================================================================
// ANNOTATION TYPE LABEL RESOLUTION
// ============================================================================

/**
 * Get annotation type label.
 *
 * DRR-189: Domain-specific label overrides (ANNOTATION_TYPE_DOMAIN_LABELS) have
 * been removed. Domain-specific terminology is now served by the backend via
 * the resolvedTerminology query and useResolvedTerminology() hook.
 *
 * This function returns the default label from ANNOTATION_TYPE_UI_CONFIG.
 * To get domain-specific labels, use useTerminology().translate() at the
 * component level instead of this config-level function.
 *
 * @param annotationType - The annotation type key (COMMENT, NOTE, etc.)
 * @param _domain - Deprecated. Ignored. Domain overrides come from backend.
 * @returns Label from annotation type config
 */
export function getAnnotationTypeLabel(
  annotationType: string,
  _domain?: string | null,
): string {
  return getAnnotationTypeConfig(annotationType).label;
}

// ============================================================================
// FILTERED TYPE OPTIONS
// ============================================================================

/**
 * Get annotation type options filtered by user scopes with domain-specific labels.
 *
 * Regular users (annotation:write only) see COMMENT and NOTE.
 * Users with annotation:escalate also see ESCALATION.
 * Users with annotation:admin see all types including DECISION.
 *
 * @param userScopes - Set of scope strings from auth context
 * @param domain - Optional tenant domain for label overrides (healthcare, financial, legal)
 * @returns Filtered type entries sorted by priority
 */
export function getAnnotationTypeOptions(
  userScopes: Set<string> | string[],
  domain?: string | null,
): Array<{ value: string; label: string }> {
  const scopes =
    userScopes instanceof Set ? userScopes : new Set(userScopes);

  return Object.entries(ANNOTATION_TYPE_UI_CONFIG)
    .filter(([, config]) => {
      if (config.requiredScope === null) return true;
      return scopes.has(config.requiredScope) || scopes.has('annotation:admin');
    })
    .sort(([, a], [, b]) => a.priority - b.priority)
    .map(([value]) => ({
      value,
      label: getAnnotationTypeLabel(value, domain),
    }));
}
